import PropTypes from 'prop-types';
import IncidentCard from '../IncidentCard';
import { calculateRiskScore, calculateTrend } from '@/utils/riskScoring';

/**
 * Intune Security View Component for SOC Dashboard
 * Displays Microsoft Intune device security status
 * 
 * @param {Object} props - Component props
 * @param {Object} props.data - Intune security data
 * @param {Object} props.previousData - Previous period data for trend calculation
 */
const IntuneSecurityView = ({ data, previousData }) => {
  if (!data) {
    return (
      <div className="soc-view intune-security-view">
        <h2 className="view-title">Intune Device Security</h2>
        <p className="no-data-message">Loading Intune data...</p>
      </div>
    );
  }

  // Calculate risk scores
  const nonCompliantScore = calculateRiskScore({
    fileCount: data.intuneNonCompliantDeviceCount || 0
  });

  const threatenedScore = calculateRiskScore({
    fileCount: data.intuneThreatenedDeviceCount || 0
  });

  // Calculate trends
  const nonCompliantTrend = previousData ? calculateTrend(
    data.intuneNonCompliantDeviceCount || 0,
    previousData.intuneNonCompliantDeviceCount || 0
  ) : null;

  const threatenedTrend = previousData ? calculateTrend(
    data.intuneThreatenedDeviceCount || 0,
    previousData.intuneThreatenedDeviceCount || 0
  ) : null;

  const totalIntuneAlerts = (data.intuneNonCompliantDeviceCount || 0) + 
                           (data.intuneThreatenedDeviceCount || 0);

  const complianceRate = data.intuneTotalDevices > 0 
    ? Math.round(((data.intuneTotalDevices - (data.intuneNonCompliantDeviceCount || 0)) / data.intuneTotalDevices) * 100)
    : 100;

  return (
    <div className="soc-view intune-security-view">
      <h2 className="view-title">Intune Device Security</h2>

      {/* Summary Header */}
      <div className="view-summary-header">
        <div className="summary-stat">
          <span className="summary-label">Total Devices</span>
          <span className="summary-value">{data.intuneTotalDevices || 0}</span>
        </div>
        <div className="summary-stat">
          <span className="summary-label">Compliance Rate</span>
          <span className="summary-value" style={{ color: complianceRate >= 90 ? '#28a745' : complianceRate >= 70 ? '#ffc107' : '#dc3545' }}>
            {complianceRate}%
          </span>
        </div>
        <div className="summary-stat">
          <span className="summary-label">Security Alerts</span>
          <span className="summary-value">{totalIntuneAlerts}</span>
        </div>
      </div>

      {/* Intune Alert Cards */}
      <div className="incident-cards-grid">
        <IncidentCard
          title="Non-Compliant Devices"
          riskScore={nonCompliantScore}
          data={{
            count: data.intuneNonCompliantDeviceCount || 0,
            criticalNonCompliance: data.intuneNonCompliantCritical || 0,
            policyViolations: data.intuneNonCompliantPolicyViolations || 0,
            outOfDateDevices: data.intuneNonCompliantOutOfDate || 0
          }}
          incidentType="Intune"
          trend={nonCompliantTrend}
          expandable={true}
        />

        <IncidentCard
          title="Devices with Threat Detections"
          riskScore={threatenedScore}
          data={{
            count: data.intuneThreatenedDeviceCount || 0,
            highSeverity: data.intuneThreatenedHigh || 0,
            mediumSeverity: data.intuneThreatenedMedium || 0,
            malwareDetections: data.intuneThreatenedMalware || 0
          }}
          incidentType="Intune"
          trend={threatenedTrend}
          expandable={true}
        />
      </div>

      {/* Device Platform Breakdown */}
      <div className="view-details-section">
        <h3>Device Platform Distribution</h3>
        <div className="platform-breakdown-grid">
          <div className="platform-stat">
            <span className="platform-icon">🪟</span>
            <span className="platform-label">Windows</span>
            <span className="platform-value">{data.intuneWindowsDevices || 0}</span>
            <span className="platform-issues">{data.intuneWindowsIssues || 0} issues</span>
          </div>
          <div className="platform-stat">
            <span className="platform-icon">🍎</span>
            <span className="platform-label">macOS</span>
            <span className="platform-value">{data.intuneMacDevices || 0}</span>
            <span className="platform-issues">{data.intuneMacIssues || 0} issues</span>
          </div>
          <div className="platform-stat">
            <span className="platform-icon">📱</span>
            <span className="platform-label">iOS</span>
            <span className="platform-value">{data.intuneiOSDevices || 0}</span>
            <span className="platform-issues">{data.intuneiOSIssues || 0} issues</span>
          </div>
          <div className="platform-stat">
            <span className="platform-icon">🤖</span>
            <span className="platform-label">Android</span>
            <span className="platform-value">{data.intuneAndroidDevices || 0}</span>
            <span className="platform-issues">{data.intuneAndroidIssues || 0} issues</span>
          </div>
        </div>
      </div>

      {/* Top Policy Violations */}
      <div className="view-details-section">
        <h3>Top Policy Violations</h3>
        <div className="violations-list">
          {data.intuneTopViolations && data.intuneTopViolations.length > 0 ? (
            <ul className="violations-items">
              {data.intuneTopViolations.slice(0, 5).map((violation, idx) => (
                <li key={idx} className="violation-item">
                  <span className="violation-name">{violation.policyName || 'Unknown Policy'}</span>
                  <span className="violation-count">{violation.count || 0} devices</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="no-data-message">No policy violations detected</p>
          )}
        </div>
      </div>
    </div>
  );
};

IntuneSecurityView.propTypes = {
  data: PropTypes.object,
  previousData: PropTypes.object
};

IntuneSecurityView.defaultProps = {
  data: null,
  previousData: null
};

export default IntuneSecurityView;
